/* eslint-disable import/no-restricted-paths */
import React from 'react';
import { connect } from 'react-redux';
import { LazyAddToCartButton } from 'core/utils/lazyCart';
import { AddToCartRequestProductItem, AnyAction, connectFromCart, modalActionCreators, ModalDisplayStatus } from '@cart-js/core';
import { OverlayType, showOverlay } from 'store/slice/overlay';
import { fetchAvailability } from 'component/page/detail/store/availabilityIndex';
import { compose, createSelector, Dispatch } from '@reduxjs/toolkit';
import { selectActivityTokenFromDetailState } from 'component/page/detail/store/detail.selectors';
import { ScreenSize, selectScreenSize } from 'store/slice/screen';
import { selectBehaviorFromBootstrap, selectIsWebViewFromBootstrap } from 'store/slice/bootstrap';
import { selectIsRebrandCartToSaved } from 'core/utils/cart';

interface OwnProps {
    productItem: AddToCartRequestProductItem;
    disabled: boolean;
    ariaLabel: string;
    // There's a linter error here that incorrectly detects that overrideShowOverlayType is unused
    // eslint-disable-next-line react/no-unused-prop-types
    overrideShowOverlayType?: OverlayType;
}

interface StateProps {
    activityTokenFromState: string;
    screenSize: ScreenSize;
    isRebrandCartToSaved: boolean;
}

interface DispatchProps {
    openCartSliderOrInterstitial: (activityToken: string) => void;
}

interface CartDispatchProps {
    setAddedToCartToastVisible: () => void;
}

type Props = OwnProps & StateProps & DispatchProps & CartDispatchProps;

const AddToCartComponent: React.FC<Props> = (props: Props) => {
    const { disabled, productItem, ariaLabel, activityTokenFromState, openCartSliderOrInterstitial } = props;

    const handleOnAddToCartSuccess = () => {
        props.isRebrandCartToSaved && !props.screenSize.mobile ? props.setAddedToCartToastVisible() : openCartSliderOrInterstitial(activityTokenFromState);
    };
    return (
        <React.Suspense fallback={null}>
            <LazyAddToCartButton
                ariaLabel={ariaLabel}
                fullWidth={!props.screenSize.desktop}
                productItem={productItem}
                onAddSucceed={handleOnAddToCartSuccess}
                disabled={disabled}
                useDroneButton={true}
                size="l"
            />
        </React.Suspense>
    );
};

AddToCartComponent.displayName = 'AddToCartComponent';

export const mapDispatchToProps = (dispatch: Dispatch, ownProps: OwnProps): DispatchProps => ({
    openCartSliderOrInterstitial: (activityToken: string) => {
        const overlay = ownProps.overrideShowOverlayType ?? OverlayType.CartSummary;
        dispatch(showOverlay(overlay));
        if (overlay !== OverlayType.CartInterstitial) {
            fetchAvailability(activityToken, { excludePassengers: true });
        }
    },
});

export const mapCartDispatchToProps = (dispatch: React.Dispatch<AnyAction>): CartDispatchProps => ({
    setAddedToCartToastVisible: () => dispatch(modalActionCreators.updateDisplayStatus(ModalDisplayStatus.AddedToCart)),
});

const mapStateToProps = createSelector(
    selectBehaviorFromBootstrap,
    selectActivityTokenFromDetailState,
    selectScreenSize,
    selectIsWebViewFromBootstrap,
    (behavior, activityTokenFromState, screenSize, isWebView): StateProps => {
        const isRebrandCartToSaved = selectIsRebrandCartToSaved(behavior, isWebView);
        return {
            activityTokenFromState,
            screenSize,
            isRebrandCartToSaved,
        };
    },
);

const AddToCart = compose<React.FC<OwnProps>>(
    connect(mapStateToProps, mapDispatchToProps),
    connectFromCart(() => ({}), mapCartDispatchToProps),
)(AddToCartComponent);

AddToCart.displayName = 'AddToCart';

export { AddToCartComponent, AddToCart };
